import { _ } from "../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import { withScrollRetained } from "../../helpers/dom-util.js";
import ForecastReportViewerWidget from "../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/ForecastReportViewerWidget.js";
import TabWidget from "../TabWidget/TabWidget.js";

/**
 * ForecastTabsWidget renders a list of Forecast reports in several
 * panels.
 *
 * @param {function} isInSaveSpaceMode - Whether the reports should be
 *   visually reduced to fit small screens
 * @param {function} onCellClick - Callback used when a Forecast cell
 *   is clicked
 * @param {function} onTabClicked - Called when clicking on another tab
 * @param {Forecast} forecast - The model from which reports are gotten
 * @param {object[]} extraTabs - Other tabs that should be shown after
 *   the report tabs. See {@link TabWidget} for the tabs content
 * @param {renderable} actions - Anything that should be rendered
 *   alongside the tab names. Typically a settings button
 * @param {ForecastAssumptionsWidget} [assumptionsWidget] - If
 *   provided, an "Assumptions" tab is added and will show this
 *   widget. In practice, this parameter can contain any renderable
 *   and doesn't have to be in instance of ForecastAssumptionsWidget
 */
export default class ForecastTabsWidget extends Widget {
	constructor({
		isInSaveSpaceMode,
		onCellClick,
		forecast,
		assumptionsWidget,
		extraTabs = [],
		onTabClicked,
		actions,
	}) {
		super(...arguments);

		this._isInSaveSpaceMode = isInSaveSpaceMode;
		this._onCellClick = onCellClick;

		this._assumptionsWidget = assumptionsWidget;
		this._onTabClicked = onTabClicked;

		this._tabWidget = new TabWidget({
			tabs: [...this._makeTabs({ forecast }), ...extraTabs],
			onTabClicked: () => onTabClicked(this.getCurrentReport()),
			actions,
		});
	}

	setCurrentTabFromId(tabId) {
		this._tabWidget.setActiveTabWithId(tabId);
	}

	setCurrentTabFromReport(report) {
		this.setCurrentTabFromId(`tab-report-${report.getId()}`);
	}

	selectCell({ row, column }) {
		const reportViewer = this.getCurrentViewer();
		reportViewer.selectCell({ row, column });
	}

	selectReportAndCell({ report, row, column }) {
		if (!this._isCurrentReport(report)) {
			this.setCurrentTabFromReport(report);
			this._onTabClicked(report);
		}

		this.selectCell({ row, column });
		this.scrollToSelectedCell();
		this._onCellClick({ report, row, column });
	}

	scrollToSelectedCell() {
		const reportViewer = this.getCurrentViewer();

		const stickyColumnWidth = this.asJQuery()
			.find("tr:first-child th")
			.get(0).clientWidth;

		reportViewer.scrollToSelectedCell({
			leftShift: stickyColumnWidth,
		});
	}

	getCurrentReport() {
		return this.getCurrentViewer().getReport?.();
	}

	getAllReports() {
		return this._getAllViewers().map((viewer) => viewer.getReport());
	}

	updateCurrentReport() {
		if (!this.getCurrentReport()) return;

		withScrollRetained({
			fn: () => this.getCurrentViewer().update(),
			getDOMNode: () => this.asJQuery().find(".reportViewer").get(0),
		});
	}

	updateSaveSpaceMode() {
		withScrollRetained({
			fn: () => {
				const saveSpaceMode = this._isInSaveSpaceMode();

				this._getAllViewers().forEach((viewer) =>
					viewer.setSaveSpaceMode(saveSpaceMode),
				);
			},
			getDOMNode: () => this.asJQuery().find(".reportViewer").get(0),
		});
	}

	renderContentOn(html) {
		html.div({ class: "ForecastTabsWidget" }, this._tabWidget);
	}

	getCurrentViewer() {
		return this._tabWidget.getActiveContent();
	}

	_getAllViewers() {
		return this._tabWidget
			.getAllContents()
			.filter(
				(content) => content.constructor === ForecastReportViewerWidget,
			);
	}

	_makeTabs({ forecast }) {
		const reportTabs = forecast
			.getReportsAsList()
			.map((report) => this._makeTabForReport(report));

		return this._assumptionsWidget
			? [...reportTabs, this._makeTabForAssumptions()]
			: reportTabs;
	}

	_makeTabForReport(report) {
		const content = new ForecastReportViewerWidget({
			report,
			onCellClick: ({ row, column }) =>
				this._onCellClick({ row, column, report }),
			saveSpaceMode: this._isInSaveSpaceMode(),
		});

		content.onAttach.register(() => this._scrollToCurrentPeriod());

		return {
			name: report.getTitle(),
			id: `tab-report-${report.getId()}`,
			content,
			"data-help": `tab-report-${report.getId()}`,
		};
	}

	_makeTabForAssumptions() {
		return {
			name: _("Assumptions"),
			id: "tab-assumptions",
			"data-help": "tab-assumptions",
			content: this._assumptionsWidget,
		};
	}

	_scrollToCurrentPeriod() {
		const currentHeaderPosition = this.asJQuery()
			.find("th.current-period")
			.position();

		if (!currentHeaderPosition) {
			return;
		}

		// The account column is sticky, we need to scroll after its width
		const accountColumnWidth = this.asJQuery()
			.find("th.report-title")
			.get(0).clientWidth;

		const position = currentHeaderPosition.left - accountColumnWidth;

		this.asJQuery().find(".forecast-report").scrollLeft(position);
	}

	_isCurrentReport(report) {
		const currentReport = this.getCurrentReport();
		return report.getId() === currentReport?.getId();
	}
}
